import React, { useState } from 'react';
import {
  TABLE_PRIORITY_FIELDS, isHiddenField, STATUS_BADGE, STATUS_LABEL,
} from './sandboxConfig';

const MAX_DEPTH = 2;

function JsonFallback({ value }) {
  return (
    <pre className="font-mono text-[11px] text-surface-500 bg-surface-50 rounded px-2 py-1.5 overflow-x-auto whitespace-pre-wrap break-all">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function Value({ fieldKey, value, depth = 0 }) {
  if (value === null || value === undefined) {
    return <span className="text-surface-300 italic text-xs">null</span>;
  }
  if (fieldKey === 'status' && typeof value === 'string') {
    return (
      <span className={`badge status-badge ${STATUS_BADGE[value] || 'badge-neutral'} text-[10px] py-0`}>
        {STATUS_LABEL[value] || value}
      </span>
    );
  }
  if (typeof value === 'boolean') {
    return (
      <span className={`badge ${value ? 'badge-success' : 'badge-neutral'} text-[10px] py-0 w-fit`}>
        {String(value)}
      </span>
    );
  }
  if (typeof value === 'number') {
    return <span className="font-mono text-xs text-surface-700">{value}</span>;
  }
  if (fieldKey === 'download_url' && typeof value === 'string') {
    return (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-accent-600 hover:text-accent-700 font-medium"
      >
        Download
      </a>
    );
  }
  if (typeof value === 'object' && depth >= MAX_DEPTH) {
    return <JsonFallback value={value} />;
  }
  if (Array.isArray(value)) {
    return <ArrayValue items={value} depth={depth} />;
  }
  if (typeof value === 'object') {
    return <ObjectValue value={value} depth={depth} />;
  }
  return <span className="text-xs text-surface-700 break-all">{String(value)}</span>;
}

function ObjectValue({ value, depth }) {
  const keys = Object.keys(value);
  return (
    <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 py-0.5 items-baseline">
      {keys.map((k) => (
        <React.Fragment key={k}>
          <span className="font-mono text-[11px] text-surface-400 truncate leading-[18px]" title={k}>{k}</span>
          <div className="min-w-0 leading-[18px]">
            <Value fieldKey={k} value={value[k]} depth={depth + 1} />
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function ArrayValue({ items, depth }) {
  const [expanded, setExpanded] = useState(items.length <= 3);

  if (items.length === 0) {
    return <span className="text-surface-300 italic text-xs">empty</span>;
  }

  // array of primitives
  if (typeof items[0] !== 'object' || items[0] === null) {
    return (
      <div className="flex flex-wrap gap-1">
        {items.map((v, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className="font-mono text-xs bg-surface-100 text-surface-600 px-1.5 py-0.5 rounded">
            {String(v)}
          </span>
        ))}
      </div>
    );
  }

  const visible = expanded ? items : items.slice(0, 2);
  const allKeys = [...new Set(items.flatMap(Object.keys))];

  return (
    <div className="space-y-1.5">
      {visible.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="rounded-md border border-surface-200 bg-surface-0 px-3 py-2">
          <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 items-baseline">
            {allKeys.map((k) => (
              <React.Fragment key={k}>
                <span className="font-mono text-[11px] text-surface-400 truncate leading-[18px]" title={k}>{k}</span>
                <div className="min-w-0 leading-[18px]">
                  <Value fieldKey={k} value={item[k]} depth={depth + 1} />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}
      {!expanded && items.length > 2 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-[11px] text-accent-600 hover:text-accent-700 font-medium"
        >
          + {items.length - 2} more
        </button>
      )}
    </div>
  );
}

function FieldRow({ fieldKey, value }) {
  const isNested = value !== null && typeof value === 'object';

  return (
    <div className={`${isNested ? 'py-3' : 'py-2'} border-b border-surface-100 last:border-0 grid grid-cols-[140px_1fr] gap-x-4 items-start`}>
      <span className="font-mono text-xs text-surface-400 pt-0.5 truncate" title={fieldKey}>
        {fieldKey}
      </span>
      <div className="min-w-0">
        <Value fieldKey={fieldKey} value={value} depth={0} />
      </div>
    </div>
  );
}

function ResourceTable({ row, resourceKey }) {
  const priority = TABLE_PRIORITY_FIELDS[resourceKey] || [];
  const allKeys = Object.keys(row).filter((k) => !isHiddenField(k));

  const scalarKeys = allKeys.filter((k) => {
    const v = row[k];
    return v === null || v === undefined || typeof v !== 'object';
  });
  const nestedKeys = allKeys.filter((k) => {
    const v = row[k];
    return v !== null && v !== undefined && typeof v === 'object';
  }).sort();

  const priorityScalar = priority.filter((k) => scalarKeys.includes(k));
  const remainingScalar = scalarKeys.filter((k) => !priority.includes(k)).sort();
  const orderedScalar = [...priorityScalar, ...remainingScalar];

  return (
    <div className="space-y-0">
      {orderedScalar.map((key) => (
        <FieldRow key={key} fieldKey={key} value={row[key]} />
      ))}
      {nestedKeys.length > 0 && orderedScalar.length > 0 && (
        <div className="border-b border-surface-200 my-1" />
      )}
      {nestedKeys.map((key) => (
        <FieldRow key={key} fieldKey={key} value={row[key]} />
      ))}
    </div>
  );
}

export default ResourceTable;
